import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { CapacitorSwipeBackPlugin } from 'capacitor-swipe-back-plugin';

/**
 * Enables iOS WKWebView's native left-edge swipe-back gesture app-wide via
 * `capacitor-swipe-back-plugin`, which sets `webView.allowsBackForwardNavigationGestures = true`.
 * The native gesture fires `webView.goBack()`, popstate fires, react-router v6 handles it.
 *
 * No-op on web/Android; gates on `Capacitor.getPlatform() === 'ios'`.
 *
 * Suspended while a ProPageModal (`.pro-sheet`) is open so the gesture closes
 * the modal-overlaid expectation rather than navigating the underlying page
 * out from under it. A MutationObserver toggles enable/disable as sheets
 * mount/unmount.
 */
export function useProSwipeBack(): void {
  useEffect(() => {
    if (Capacitor.getPlatform() !== 'ios') return;
    if (!Capacitor.isPluginAvailable('CapacitorSwipeBackPlugin')) {
      console.error('[swipe-back] plugin not registered — iOS rebuild needed');
      return;
    }

    let enabled = false;
    const enable = () => {
      if (enabled) return;
      enabled = true;
      CapacitorSwipeBackPlugin.enable().catch((e) =>
        console.error('[swipe-back] enable failed', e),
      );
    };
    const disable = () => {
      if (!enabled) return;
      enabled = false;
      CapacitorSwipeBackPlugin.disable().catch((e) =>
        console.error('[swipe-back] disable failed', e),
      );
    };

    enable();

    const sync = () => {
      if (document.querySelector('.pro-sheet') != null) disable();
      else enable();
    };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      disable();
    };
  }, []);
}
